/* Tailwind v4: single @import replaces @tailwind base/components/utilities */
@import 'tailwindcss';

/* ── Design tokens ──────────────────────────────────────────────────────────── */
:root {
  /* Colores base — TEMA CLARO (marca) */
  --color-bg:        #F4F6F8;   /* gris muy claro (fondo) */
  --color-surface:   #FFFFFF;   /* tarjetas */
  --color-surface-2: #EEF1F4;   /* inputs / zonas suaves */
  --color-border:    #DDE3EA;
  --color-border-2:  #C8D0DA;

  /* ── Texto — tres niveles SEPARADOS, los tres legibles ──────────────────
     Decisión A de la fase 05, de Manuel. El terciario (#8A929C) no llegaba a AA
     en ninguno de los tres fondos —3,15 / 2,91 / 2,78— y era el gris más usado
     del panel: la fecha de la cabecera, los rótulos de columna, «0 registros» y
     **«Cerrar sesión»**. No era decoración; era información que hay que leer.

     El problema no se arreglaba solo oscureciéndolo: el terciario que aprueba
     (#656D77) queda a tres puntos de luminosidad del secundario de entonces
     (#5C6470), y dos grises que no se distinguen no son una jerarquía. Así que
     baja también el de en medio. Ahora hay un escalón visible entre los tres y
     los tres pasan AA sobre blanco, sobre --color-bg y sobre --color-surface-2,
     que es el fondo más exigente. El coste, dicho: el panel se ve algo más
     contrastado que antes. Es una consola de operación densa —rótulo, valor,
     metadato—, y esa jerarquía de tres está haciendo trabajo real. */
  --color-text:      #1E232A;   /* 15,80 · 14,59 · 13,94 */
  --color-text-2:    #4A515B;   /*  8,02 ·  7,40 ·  7,07  (era #5C6470) */
  --color-text-3:    #656D77;   /*  5,24 ·  4,84 ·  4,62  (era #8A929C) */

  /* ── Acento — el coral, ahora hondo ─────────────────────────────────────
     Decisión B de la fase 05, de Manuel: «el relleno pasa a #C43A1C y la letra
     sigue blanca», con el coste asumido de que el panel pierde el coral vivo.

     Se aplica al ACENTO ENTERO, no solo al relleno del botón, y la razón es que
     medí el resto antes de decidir dónde paraba: el #FF5E3A vivo **tampoco
     llegaba a 3:1 como borde** sobre los fondos no blancos del panel —2,80
     sobre --color-bg y 2,68 sobre --color-surface-2, el umbral no textual de
     WCAG—. Dejarlo vivo en los anillos de foco y los bordes habría conservado
     el color a cambio de un agujero que esta suite no mide. El hondo pasa los
     tres: 5,29 · 4,88 · 4,67.

     `--color-accent-ink` desaparece: existía para escribir coral legible cuando
     el acento no lo era. Ahora el acento lo es, y un token que dice «la versión
     buena de este otro» solo tiene sentido mientras haya una mala. */
  --rgb-accent:      196 58 28;
  --color-accent:    rgb(var(--rgb-accent));   /* #C43A1C · relleno, texto, borde y foco */
  --color-accent-2:  #A62D0F;   /* hover: MÁS oscuro, nunca más claro (6,99 con blanco) */

  /* ⚠️ El tinte es OPACO a propósito, y esto corrige una medida mía de la fase 01.
     Escribí que el ítem activo del menú daba «4,64:1» con el tinte al 12 %. Era
     cierto SOBRE BLANCO y solo ahí: el mismo tinte translúcido sobre --color-bg
     da 4,30 y sobre --color-surface-2 baja a 4,12. Un tinte que cambia de valor
     según lo que tenga debajo no se puede prometer. Fijado al resultado sobre
     blanco —el coral VIVO al 12 %, que es lo único que sobrevive de él— para que
     valga 4,63 lo pongas donde lo pongas. */
  --color-accent-soft: #FFECE7;

  --color-on-accent: #FFFFFF;   /* 5,29 sobre el relleno */

  /* ── Semáforo — el sólido y sus tintes salen del MISMO número ───────────
     Cada estado tiene tres formas: el sólido (texto, punto), el tinte al 10 %
     (fondo de la píldora) y la línea al 22 % (borde del control). En la fase 01
     escribí aquí que «el tinte es el propio sólido al 10 %, así que la pareja no
     puede desalinearse». Era falso: los tres estaban escritos a mano, y nada
     impedía cambiar uno y olvidar los otros — el §4 de ERRORES_RECURRENTES, dos
     escrituras de la misma regla, dentro de un comentario que afirmaba lo
     contrario. Ahora el sólido existe UNA vez, como terna RGB, y las tres formas
     se derivan de ella. La promesa pasó a ser estructural.

     Los valores vienen de medir contra los tres fondos del panel (blanco,
     --color-bg y --color-surface-2), no de estimar: `ok` daba 4,42:1 sobre la
     zona suave y `warn` 4,43:1 — los dos por debajo de AA por poco, que es
     justo por lo que nadie los había mirado. Oscurecidos a 5,74 y 5,26. */
  --rgb-ok:        20 108 52;     /* era #15803d */
  --rgb-warn:     162  74  8;     /* era #b45309 */
  --rgb-error:    200  30  30;    /* era #dc2626: 4,13:1 sobre su propio tinte */
  --rgb-running:   37  99 235;
  --rgb-neutral:   75  85  99;
  --rgb-highlight:124  58 237;

  --color-ok:             rgb(var(--rgb-ok));
  --color-ok-soft:        rgb(var(--rgb-ok) / 0.10);
  --color-ok-line:        rgb(var(--rgb-ok) / 0.22);
  --color-warn:           rgb(var(--rgb-warn));
  --color-warn-soft:      rgb(var(--rgb-warn) / 0.10);
  --color-warn-line:      rgb(var(--rgb-warn) / 0.22);
  --color-error:          rgb(var(--rgb-error));
  --color-error-soft:     rgb(var(--rgb-error) / 0.10);
  --color-error-line:     rgb(var(--rgb-error) / 0.22);   /* borde del control destructivo */
  --color-error-hover:    rgb(var(--rgb-error) / 0.18);   /* su reposo->hover: sin esto no hay respuesta al puntero */
  --color-running:        rgb(var(--rgb-running));
  --color-running-soft:   rgb(var(--rgb-running) / 0.10);
  --color-running-line:   rgb(var(--rgb-running) / 0.22);
  --color-neutral:        rgb(var(--rgb-neutral));   /* estado inactivo / sin dato */
  --color-neutral-soft:   rgb(var(--rgb-neutral) / 0.10);
  --color-highlight:      rgb(var(--rgb-highlight));   /* distintivo, hoy solo el tier premium */
  --color-highlight-soft: rgb(var(--rgb-highlight) / 0.10);

  /* La banda que Recharts pinta bajo el puntero. Estaba escrita a mano como
     `rgba(16,185,129,0.05)` en seis gráficos: esmeralda de la paleta anterior,
     sobre un panel coral. Es un realce neutro, no un estado. */
  --color-cursor: rgb(30 35 42 / 0.05);

  /* El apoyo de las filas de ámbito NEGOCIO en el parte (08-09-2026). Es tinta
     rebajada, NO un color de estado: los tres de estado (ok/warn/error) dicen
     gravedad y el coral dice «esto se pulsa». Un quinto color aquí competiría
     con los cuatro y el parte se leería peor, no mejor. */
  --color-banda-negocio: rgb(30 35 42 / 0.04);

  /* Rampa del mapa de calor de Uso: una sola familia, la del panel. Era la
     esmeralda vieja, el último sitio donde seguía viva como color de datos. */
  --rgb-heat: 255 94 58;

  /* Tipografía — Outfit (marca), Lato (cuerpo), DM Mono (cifras) */
  --font-ui:    'Lato', system-ui, sans-serif;
  --font-brand: 'Outfit', system-ui, sans-serif;
  --font-mono:  'DM Mono', 'Courier New', monospace;

  /* ── Espacio — base 4. En el panel había 16 valores distintos de `gap`
     (incluidos 3, 5, 7, 9 y 11px) y 13 de `padding`: no era un ritmo, eran
     decisiones sueltas acumuladas. ─────────────────────────────────────── */
  --space-05: 2px;   /* medio paso: solo donde 4px ya es demasiado —
                        alto de píldora, separación entre ítems de menú */
  --space-1: 4px;    /* separación mínima: icono y su texto */
  --space-2: 8px;    /* dentro de un control */
  --space-3: 12px;   /* entre elementos de un grupo */
  --space-4: 16px;   /* entre grupos */
  --space-5: 24px;   /* respiro de tarjeta */
  --space-6: 32px;   /* entre secciones */
  --space-7: 40px;   /* separación mayor / estados vacíos */

  /* ── Radios — había nueve valores; 6, 7 y 8px eran 77 usos de la misma
     intención escrita de tres formas. ──────────────────────────────────── */
  --radius-sm:   6px;    /* controles: input, botón pequeño, badge */
  --radius-md:   8px;    /* tarjetas y contenedores */
  --radius-lg:   12px;   /* superficies grandes: modal, panel flotante */
  --radius-pill: 999px;  /* píldoras y puntos */

  /* ── Escala tipográfica — había 18 tamaños entre 9 y 36px, con 11/12/13/14
     conviviendo sin criterio. Estos nueve escalones cubren los 324 usos.
     El panel es denso a propósito: es una consola de operación, no una web. */
  --text-micro: 10px;  /* etiquetas mono en mayúsculas */
  --text-xs:    11px;  /* metadatos, subtítulos */
  --text-sm:    12px;  /* cuerpo secundario y cifras en tabla */
  --text-base:  13px;  /* cuerpo del panel */
  --text-md:    14px;  /* énfasis dentro del cuerpo */
  --text-lg:    18px;  /* título de sección */
  --text-xl:    22px;  /* título de página */
  --text-2xl:   28px;  /* cifra de KPI */
  --text-3xl:   36px;  /* cifra heroica */

  /* Pesos — lo único que ya estaba disciplinado: solo estos cuatro en uso. */
  --weight-normal:   400;
  --weight-medium:   500;
  --weight-semibold: 600;
  --weight-bold:     700;

  /* Movimiento — desaceleración, sin rebote: esto es una herramienta. */
  --ease:   cubic-bezier(0.2, 0, 0, 1);
  --t-fast: 0.1s;
  --t-base: 0.15s;
  --t-slow: 0.25s;

  /* Capas */
  --z-sticky:   10;
  --z-dropdown: 100;
  --z-modal:    1000;

  /* Sombras (suaves, para fondo claro) */
  --shadow-sm: 0 1px 2px rgba(16,24,40,0.06);
  --shadow-md: 0 4px 12px rgba(16,24,40,0.08);
  --shadow-lg: 0 8px 24px rgba(16,24,40,0.12);
  --shadow-control: 0 1px 3px rgba(0,0,0,0.28);  /* pomo del toggle: va sobre color, no sobre fondo */
}

/* ── Pantallas públicas (alta y acceso) ──────────────────────────────────────
   Todo lo que se ve ANTES de entrar al panel habla el idioma de la landing:
   Oxford, coral y Outfit. Dentro del panel se vuelve al tema claro, que es
   donde viven las tablas y los gráficos y donde está calibrado el contraste.

   No inventa nombres: REDEFINE los mismos tokens. Así un componente que usa
   var(--color-surface) o var(--text-md) sirve en los dos mundos sin saber en
   cuál está, y el día que estas pantallas cambien de tema no hay que tocar
   ningún componente.

   ⚠️ EL ACENTO VUELVE A #FF5E3A AQUÍ, Y NO ES UN DESCUIDO. El #C43A1C del
   panel se eligió para dar 4,5:1 sobre BLANCO; sobre Oxford ese mismo coral se
   apaga y cae por debajo del mínimo. El claro da ~5,4:1 sobre #1E232A. Es el
   mismo criterio de la tabla de arriba —medir sobre el fondo real— aplicado a
   un fondo distinto, no una excepción a él. */
.cm-publico {
  --color-bg:        #1E232A;
  --color-surface:   #232932;
  --color-surface-2: rgb(255 255 255 / 0.04);
  --color-border:    rgb(255 255 255 / 0.08);
  --color-border-2:  rgb(255 255 255 / 0.14);

  --color-text:      #FFFFFF;
  --color-text-2:    #B8BEC7;   /*  9,9:1 sobre #1E232A */
  --color-text-3:    #8A929C;   /*  5,6:1 */

  /* ⚠️ Se redefinen los --color-*, NO solo los --rgb-*: un `--color-accent:
     rgb(var(--rgb-accent))` declarado en :root ya está RESUELTO cuando llega
     aquí, así que cambiar la tripleta abajo no lo toca. Costó una lectura en
     el navegador descubrirlo (el foco de los campos salía rojo oscuro). */
  --rgb-accent:      255 94 58;
  --color-accent:    #FF5E3A;
  --color-accent-2:  #FF8A5C;   /* hover: MÁS claro, al revés que en el panel */
  --color-accent-soft: rgb(255 94 58 / 0.12);
  --color-on-accent: #FFFFFF;

  --rgb-ok:          52 208 124;
  --rgb-warn:        255 194 75;
  --rgb-error:       255 94 94;
  --color-ok:        rgb(var(--rgb-ok));
  --color-warn:      rgb(var(--rgb-warn));
  --color-error:     rgb(var(--rgb-error));
  --color-ok-soft:    rgb(var(--rgb-ok) / 0.10);
  --color-error-soft: rgb(var(--rgb-error) / 0.10);
  --color-error-line: rgb(var(--rgb-error) / 0.28);

  /* Se redefine sí o sí: la del tema claro es tinta oscura rebajada y sobre
     este fondo no se vería. Aquí el apoyo se hace con luz, no con tinta. */
  --color-banda-negocio: rgb(255 255 255 / 0.05);

  /* La escala del panel es de herramienta densa (cuerpo 13px). Esto lo lee un
     desconocido en la web, no un operador con la pantalla a un palmo. */
  --text-base: 14px;
  --text-md:   15px;
  --text-lg:   16px;
  --text-xl:   20px;
  --text-2xl:  24px;
  --text-3xl:  30px;

  --radius-md:  10px;
  --radius-lg:  14px;   /* el de la landing */

  --gradient-coral: linear-gradient(135deg, #FF5E3A 0%, #FF8A5C 100%);
  --shadow-coral:   0 20px 60px -20px rgb(255 94 58 / 0.55);
  --shadow-lg:      0 30px 80px -30px rgb(0 0 0 / 0.6);

  min-height: 100%;
  background-color: var(--color-bg);
  color: var(--color-text);
}

/* El aura del hero de la landing, muy contenida: profundidad sin convertirse en
   uno de esos degradados que se comen la página. */
.cm-publico::before {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(60% 40% at 50% 0%, rgb(255 94 58 / 0.14) 0%, rgb(255 94 58 / 0) 70%);
}

.cm-publico input::placeholder { color: rgb(255 255 255 / 0.34); }
.cm-publico input:-webkit-autofill {
  -webkit-text-fill-color: var(--color-text);
  -webkit-box-shadow: 0 0 0 1000px #2A303A inset;
}

/* Alta: dos columnas en escritorio. En móvil se apilan y la ficha del plan sube
   ARRIBA — es donde hace de recordatorio de lo que se acaba de pulsar en la
   landing. Va en CSS porque una media query no cabe en un estilo inline. */
.cm-rejilla-alta {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 72px;
  align-items: start;
}
@media (max-width: 900px) {
  .cm-rejilla-alta { grid-template-columns: minmax(0, 1fr); gap: var(--space-6); }
  .cm-rejilla-alta > .cm-col-resumen { order: -1; }
}

/* Datos a la izquierda, explicación en una columna estrecha a la derecha
   (Manuel, 31-08, opción C). Antes el texto iba DEBAJO del botón, a 78
   caracteres por línea, y empujaba la tabla media pantalla hacia abajo
   mientras el lado derecho quedaba vacío. Va en CSS por la media query: por
   debajo de 1100px vuelve a apilarse, y entonces la explicación va primero —
   en pantalla estrecha se lee antes de pulsar, que es su momento.

   Nació en la Sonda de tiendas y el 31-08 (segunda ronda) se llevó también a
   las tarjetas de la ficha —Diagnóstico del catálogo y Convenios en los que
   factura—, así que el nombre ya no menciona la sonda: es EL reparto de
   «acción + resultado» y «para qué sirve» del panel, y vive en un solo sitio
   (§4 de docs/ERRORES_RECURRENTES.md). */
.cm-rejilla-nota {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: var(--space-6);
  align-items: start;
}
@media (max-width: 1100px) {
  .cm-rejilla-nota { grid-template-columns: minmax(0, 1fr); gap: var(--space-4); }
  .cm-rejilla-nota > .cm-nota-lateral { order: -1; }
}

/* ── Base ───────────────────────────────────────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

html, body, #root {
  height: 100%;
  margin: 0;
  padding: 0;
}

body {
  font-family: var(--font-ui);
  background-color: var(--color-bg);
  color: var(--color-text);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ── Scrollbar custom ────────────────────────────────────────────────────────── */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: var(--color-surface); }
::-webkit-scrollbar-thumb { background: var(--color-border-2); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: var(--color-text-3); }

/* ── Mono numbers ────────────────────────────────────────────────────────────── */
.font-mono, code, .tabular-nums { font-family: var(--font-mono); }

/* ── Utility: fade-in ────────────────────────────────────────────────────────── */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }

/* ── Utility: pulse-dot (para running indicator) ─────────────────────────────── */
@keyframes pulseDot {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
}
.pulse-dot { animation: pulseDot 1.4s ease-in-out infinite; }

/* ── Utility: spin ───────────────────────────────────────────────────────────
   Button (estado `loading`) y Table ("Cargando…") llevaban desde siempre
   `animation: spin 0.7s linear infinite` contra unos keyframes que NO estaban
   definidos: comprobado en el CSS compilado, solo salían fadeIn y pulseDot.
   Los indicadores de carga del panel eran anillos quietos. Un CSS que apunta a
   una animación inexistente no protesta — el fallo mudo del §5. */
@keyframes spin {
  to { transform: rotate(360deg); }
}
.animate-spin { animation: spin 0.7s linear infinite; }
